/* eslint-disable react/jsx-sort-props */
import React, { ReactElement, useState } from 'react';

import { registerMBTIUserAPI } from '@api/controller';
import { Layout } from '@components/atoms';
import { Modal } from 'antd';

import MBTIPersonalInfoTerm from './MBTIPersonalInfoTerm';

type Gender = 'MALE' | 'FEMALE';

type FormState = {
  name: string;
  gender: Gender | undefined;
  age: string;
  phone: string;
};

type Errors = Partial<Record<keyof FormState | 'terms', string>>;

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  height: '52px',
  border: '1px solid #cfcfcf',
  borderRadius: '20px',
  background: '#ffffff',
  fontSize: '18px',
  color: '#1d1c1d',
  padding: '0 20px',
  outline: 'none',
  boxSizing: 'border-box',
};

const SELECT_STYLE: React.CSSProperties = {
  ...INPUT_STYLE,
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='14' height='8' viewBox='0 0 14 8'%3e%3cpath fill='none' stroke='%23888' stroke-width='2' d='M1 1l6 6 6-6'/%3e%3c/svg%3e\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 20px center',
  paddingRight: '46px',
  cursor: 'pointer',
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: '18px',
  color: '#1d1c1d',
  letterSpacing: '-0.5px',
  fontWeight: 700,
};

const ERROR_STYLE: React.CSSProperties = {
  color: '#f35b5b',
  fontSize: '13px',
  lineHeight: '1.4em',
};

function formatPhone(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '').slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function validate(form: FormState, personalAgreed: boolean): Errors {
  const errors: Errors = {};
  if (!form.name) errors.name = '이름을 입력해 주세요.';
  else if (/\s/.test(form.name) || !/^[A-Za-z\uAC00-\uD7A3]+$/.test(form.name)) {
    errors.name = '한글/영문만 입력 가능합니다. (공백 불가)';
  }

  if (!form.gender) errors.gender = '성별을 선택해 주세요.';

  if (!form.age) errors.age = '만 나이를 입력해 주세요.';
  else {
    const n = Number(form.age);
    if (!Number.isInteger(n) || n < 1 || n > 120) {
      errors.age = '1~120 사이의 숫자를 입력해 주세요.';
    }
  }

  const digits = form.phone.replace(/[^0-9]/g, '');
  if (!digits) errors.phone = '휴대폰 번호를 입력해 주세요.';
  else if (!/^01[016789][0-9]{7,8}$/.test(digits)) {
    errors.phone = '올바른 휴대폰 번호 형식이 아닙니다.';
  }

  if (!personalAgreed) {
    errors.terms = '개인정보 처리방침에 동의해 주세요.';
  }

  return errors;
}

function MBTIRegisterPage(): ReactElement {
  const [form, setForm] = useState<FormState>({
    name: '',
    gender: undefined,
    age: '',
    phone: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [personalAgreed, setPersonalAgreed] = useState(false);
  const [termOpen, setTermOpen] = useState(false);

  const onSubmit = async () => {
    const v = validate(form, personalAgreed);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setSubmitting(true);
    const res = await registerMBTIUserAPI({
      name: form.name,
      gender: form.gender as Gender,
      age: Number(form.age),
      phone: form.phone.replace(/[^0-9]/g, ''),
    });
    setSubmitting(false);

    if (res && (res as any).success) {
      setCompleted(true);
    } else {
      setErrors({ phone: '전송에 실패했습니다. 잠시 후 다시 시도해 주세요.' });
    }
  };

  if (completed) {
    return (
      <Layout yzTypeLayout="MBTI_REGISTER_PC">
        <div
          style={{
            width: '100%',
            maxWidth: '520px',
            background: '#ffffff',
            border: '1px solid #e5ebf3',
            borderRadius: '24px',
            padding: '48px 40px',
            boxShadow: '0 8px 32px rgba(20, 40, 80, 0.06)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: '26px',
              fontWeight: 700,
              color: '#1d1c1d',
              marginBottom: '20px',
              lineHeight: '1.3em',
            }}
          >
            전송 완료
          </div>
          <div
            style={{
              fontSize: '16px',
              lineHeight: '1.6em',
              color: '#444',
              whiteSpace: 'pre-wrap',
            }}
          >
            {`입력하신 번호로 카카오톡 알림톡이 발송됩니다.
링크 발송에는 최대 1분 정도 소요될 수 있어요.

알림톡이 오지 않는다면
'(증상설문)예진' 채널의 차단 해제를 부탁드립니다.`}
          </div>
        </div>
      </Layout>
    );
  }

  const submitDisabled = submitting || !personalAgreed;

  return (
    <Layout yzTypeLayout="MBTI_REGISTER_PC">
      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          background: '#ffffff',
          border: '1px solid #e5ebf3',
          borderRadius: '24px',
          padding: '48px 40px',
          boxShadow: '0 8px 32px rgba(20, 40, 80, 0.06)',
          display: 'flex',
          flexDirection: 'column',
          rowGap: '22px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <div
            style={{
              fontSize: '26px',
              fontWeight: 700,
              color: '#1d1c1d',
              lineHeight: '1.3em',
              marginBottom: '8px',
            }}
          >
            한의진단 MBTI
          </div>
          <div style={{ fontSize: '14px', color: '#888' }}>
            정보를 입력하시면 카카오톡으로 설문 링크를 보내드려요.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}>
          <span style={LABEL_STYLE}>이름</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="예) 홍길동"
            style={INPUT_STYLE}
            type="text"
          />
          {errors.name && <span style={ERROR_STYLE}>{errors.name}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}>
          <span style={LABEL_STYLE}>성별</span>
          <select
            value={form.gender ?? ''}
            onChange={(e) =>
              setForm({ ...form, gender: (e.target.value || undefined) as Gender | undefined })
            }
            style={{
              ...SELECT_STYLE,
              color: form.gender ? '#1d1c1d' : '#888',
            }}
          >
            <option value="" disabled>
              성별 선택
            </option>
            <option value="MALE">남자</option>
            <option value="FEMALE">여자</option>
          </select>
          {errors.gender && <span style={ERROR_STYLE}>{errors.gender}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}>
          <span style={LABEL_STYLE}>만 나이</span>
          <input
            value={form.age}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
              setForm({ ...form, age: digits });
            }}
            placeholder="예) 30"
            style={INPUT_STYLE}
            type="text"
            inputMode="numeric"
          />
          {errors.age && <span style={ERROR_STYLE}>{errors.age}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}>
          <span style={LABEL_STYLE}>휴대폰 번호</span>
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
            placeholder="010-1234-5678"
            style={INPUT_STYLE}
            type="text"
            inputMode="numeric"
          />
          {errors.phone && <span style={ERROR_STYLE}>{errors.phone}</span>}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            background: '#f7f9fc',
            borderRadius: '14px',
            border: '1px solid #eef2f8',
          }}
        >
          <button
            type="button"
            onClick={() => setPersonalAgreed((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '8px',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <img
              alt={personalAgreed ? 'checked' : 'unchecked'}
              src={`${process.env.PUBLIC_URL}/img/${personalAgreed ? 'checked-blue' : 'unchecked-icon'}.webp`}
              style={{ width: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#1d1c1d', fontWeight: 500 }}>
              (필수) 개인정보 처리방침 동의
            </span>
          </button>
          <button
            type="button"
            onClick={() => setTermOpen(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              fontSize: '12px',
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            자세히보기
          </button>
        </div>
        {errors.terms && <span style={ERROR_STYLE}>{errors.terms}</span>}

        <button
          type="button"
          onClick={onSubmit}
          disabled={submitDisabled}
          style={{
            marginTop: '8px',
            width: '100%',
            height: '56px',
            border: 'none',
            borderRadius: '20px',
            background: submitDisabled ? '#9bb6d9' : '#164ee6',
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: 700,
            cursor: submitDisabled ? 'not-allowed' : 'pointer',
            letterSpacing: '-0.3px',
          }}
        >
          {submitting ? '전송 중…' : '카카오톡으로 링크 받기'}
        </button>
      </div>

      <Modal
        visible={termOpen}
        onCancel={() => setTermOpen(false)}
        footer={null}
        width={720}
        title="개인정보 처리방침"
        bodyStyle={{
          maxHeight: 'calc(80vh - 110px)',
          overflowY: 'auto',
          padding: '24px 28px',
        }}
        destroyOnClose
        centered
      >
        <MBTIPersonalInfoTerm />
      </Modal>
    </Layout>
  );
}

export default MBTIRegisterPage;
